import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchPassport,
  PassportResult,
  ProServiceReport,
  ZendeskTicket,
  ZendeskComment,
  FabricodeAlarmReport,
  FabricodeMachineData,
} from '../../services/passportService';
import { renderMarkdown } from '../../utils/markdown';
import './PassportPanel.css';

type TabId = 'proservice' | 'fabricode' | 'zendesk';

// ── ProService tab ─────────────────────────────────────────────────────────

function ProServiceTab({ data }: { data: PassportResult['proservice'] }) {
  const { t } = useTranslation();
  if (!data) return <div className="passport-tab-empty">{t('passport.proservice.noData')}</div>;
  if (data.reports.length === 0) return <div className="passport-tab-empty">{t('passport.proservice.noReports')}</div>;

  return (
    <div className="passport-proservice-list">
      {data.reports.map((r: ProServiceReport, i: number) => (
        <div key={i} className="passport-report-card">
          <div className="passport-report-header">
            <span className="passport-report-number">{r.reportNumber ?? '—'}</span>
            {r.startDate && <span className="passport-report-date">{r.startDate}</span>}
            {r.daysAgo != null && <span className="passport-report-age">{r.daysAgo} {t('passport.proservice.daysAgo')}</span>}
          </div>
          <div className="passport-report-meta">
            {r.technicianName && <span><strong>{t('passport.proservice.technician')}</strong> {r.technicianName}</span>}
            {r.customerName && <span><strong>{t('passport.proservice.customer')}</strong> {r.customerName}</span>}
            {r.machineDescription && <span><strong>{t('passport.proservice.machine')}</strong> {r.machineDescription}</span>}
          </div>
          {r.problemDescription && (
            <div className="passport-report-section">
              <div className="passport-report-section-label">{t('passport.proservice.problem')}</div>
              <div className="passport-report-section-text">{r.problemDescription}</div>
            </div>
          )}
          {r.workDescription && (
            <div className="passport-report-section">
              <div className="passport-report-section-label">{t('passport.proservice.workDone')}</div>
              <div className="passport-report-section-text">{r.workDescription}</div>
            </div>
          )}
          {r.futureWorkDescription && (
            <div className="passport-report-section">
              <div className="passport-report-section-label">{t('passport.proservice.futureWork')}</div>
              <div className="passport-report-section-text">{r.futureWorkDescription}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Fabricode tab ──────────────────────────────────────────────────────────

function FabricodeTab({ data }: { data: PassportResult['fabricode'] }) {
  const { t } = useTranslation();
  if (!data) return <div className="passport-tab-empty">{t('passport.fabricode.noData')}</div>;
  if (data.notFound) return <div className="passport-tab-empty">{t('passport.fabricode.notFound')}</div>;

  if (data.multipleMatches && data.multipleMatches.length > 0) {
    return (
      <div className="passport-fabricode-section">
        <div className="passport-tab-empty">
          {t('passport.fabricode.multipleMatches', { count: data.multipleMatches.length })}
        </div>
        {data.multipleMatches.map((m, i) => (
          <div key={i} className="passport-report-card">
            <div className="passport-report-header">
              <span className="passport-report-number">ID {m.machineId}</span>
              <span className="passport-report-date">{m.serialNumber}</span>
            </div>
            {m.description && <div className="passport-report-meta"><span>{m.description}</span></div>}
          </div>
        ))}
      </div>
    );
  }

  const machine: FabricodeMachineData | null = data.machine;
  const alarm: FabricodeAlarmReport | null = data.alarmReport;

  return (
    <div className="passport-fabricode-section">
      {machine && (
        <div className="passport-fabricode-card">
          <div className="passport-fabricode-card-title">{t('passport.fabricode.machineDataTitle')}</div>
          <div className="passport-fabricode-grid">
            {machine.name && <div className="passport-fabricode-field"><span className="passport-fabricode-label">{t('passport.fabricode.fields.name')}</span><span>{machine.name}</span></div>}
            {machine.serialNumber && <div className="passport-fabricode-field"><span className="passport-fabricode-label">{t('passport.fabricode.fields.serial')}</span><span className="passport-mono">{machine.serialNumber}</span></div>}
            {machine.machineType && <div className="passport-fabricode-field"><span className="passport-fabricode-label">{t('passport.fabricode.fields.type')}</span><span>{machine.machineType}</span></div>}
            {machine.year && <div className="passport-fabricode-field"><span className="passport-fabricode-label">{t('passport.fabricode.fields.year')}</span><span>{machine.year}</span></div>}
            {machine.function && <div className="passport-fabricode-field"><span className="passport-fabricode-label">{t('passport.fabricode.fields.function')}</span><span>{machine.function}</span></div>}
            {machine.operator && <div className="passport-fabricode-field"><span className="passport-fabricode-label">{t('passport.fabricode.fields.operator')}</span><span>{machine.operator}</span></div>}
          </div>
        </div>
      )}

      {alarm && (
        <div className="passport-fabricode-card">
          <div className="passport-fabricode-card-title">
            {t('passport.fabricode.alarmReportTitle')}
            {alarm.isAnomaly && <span className="passport-anomaly-badge">{t('passport.fabricode.anomaly')}</span>}
          </div>
          <div className="passport-fabricode-grid">
            <div className="passport-fabricode-field"><span className="passport-fabricode-label">{t('passport.fabricode.fields.last7Days')}</span><span className="passport-stat">{alarm.last7DaysTotal}</span></div>
            <div className="passport-fabricode-field"><span className="passport-fabricode-label">{t('passport.fabricode.fields.historicalAvg')}</span><span className="passport-stat">{alarm.historicalWeeklyAverage.toFixed(1)}</span></div>
            <div className="passport-fabricode-field"><span className="passport-fabricode-label">{t('passport.fabricode.fields.weeksAnalysed')}</span><span className="passport-stat">{alarm.historicalWeeksAnalysed}</span></div>
            <div className="passport-fabricode-field"><span className="passport-fabricode-label">{t('passport.fabricode.fields.anomalyRatio')}</span><span className="passport-stat">{alarm.anomalyRatio.toFixed(2)}×</span></div>
          </div>
          {alarm.alarmBreakdown.length > 0 && (
            <div className="passport-alarm-breakdown">
              <div className="passport-fabricode-label" style={{ marginBottom: 6 }}>{t('passport.fabricode.fields.alarmBreakdown')}</div>
              {alarm.alarmBreakdown.map((a, i) => (
                <div key={i} className="passport-alarm-row">
                  <span className="passport-alarm-desc">{a.description}</span>
                  <span className="passport-alarm-count">{a.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!machine && !alarm && <div className="passport-tab-empty">{t('passport.fabricode.noDetails')}</div>}
    </div>
  );
}

// ── Zendesk tab ────────────────────────────────────────────────────────────

const STATUS_CLASS: Record<string, string> = {
  open: 'passport-status-open',
  pending: 'passport-status-pending',
  solved: 'passport-status-solved',
  closed: 'passport-status-closed',
};

function ZendeskComments({ comments }: { comments: ZendeskComment[] }) {
  const { i18n } = useTranslation();
  const publicComments = comments.filter((c) => c.public !== false);
  if (publicComments.length === 0) return null;
  const locale = i18n.language === 'it' ? 'it-IT' : 'en-GB';

  return (
    <div className="passport-comments-list">
      {publicComments.map((c: ZendeskComment) => (
        <div key={c.id} className="passport-comment">
          <div className="passport-comment-meta">
            {c.created_at && (
              <span className="passport-comment-date">
                {new Date(c.created_at).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' })}
              </span>
            )}
            {c.author_id && <span className="passport-comment-author">#{c.author_id}</span>}
          </div>
          {c.body && (
            <div
              className="passport-comment-body"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(c.body) }}
            />
          )}
          {c.attachments && c.attachments.length > 0 && (
            <div className="passport-comment-attachments">
              {c.attachments.map((a) => (
                <a
                  key={a.id}
                  href={a.content_url}
                  target="_blank"
                  rel="noreferrer"
                  className="passport-attachment-link"
                >
                  <span className="material-icons" style={{ fontSize: 14 }}>attach_file</span>
                  {a.file_name}
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ZendeskTicketCard({ ticket }: { ticket: ZendeskTicket }) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const publicCount = ticket.comments?.filter((c) => c.public !== false).length ?? 0;
  const hasComments = publicCount > 0;

  return (
    <div className="passport-report-card">
      <div className="passport-report-header">
        <span className="passport-report-number">#{ticket.id}</span>
        {ticket.status && (
          <span className={`passport-ticket-status ${STATUS_CLASS[ticket.status] ?? ''}`}>
            {ticket.status}
          </span>
        )}
        {ticket.priority && <span className="passport-report-age">{ticket.priority}</span>}
        {ticket.created_at && (
          <span className="passport-report-date">
            {new Date(ticket.created_at).toLocaleDateString(i18n.language === 'it' ? 'it-IT' : 'en-GB')}
          </span>
        )}
      </div>
      {ticket.subject && <div className="passport-report-meta"><span>{ticket.subject}</span></div>}
      {ticket.description && (
        <div className="passport-report-section">
          <div className="passport-report-section-label">{t('passport.zendesk.description')}</div>
          <div
            className="passport-report-section-text"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(ticket.description) }}
          />
        </div>
      )}
      {hasComments && (
        <button
          className="passport-comments-toggle"
          onClick={() => setExpanded((v) => !v)}
        >
          <span className="material-icons" style={{ fontSize: 14 }}>
            {expanded ? 'expand_less' : 'expand_more'}
          </span>
          {expanded
            ? t('passport.zendesk.hideComments')
            : t('passport.zendesk.showComments', { count: publicCount })}
        </button>
      )}
      {expanded && hasComments && <ZendeskComments comments={ticket.comments!} />}
    </div>
  );
}

function ZendeskTab({ data }: { data: PassportResult['zendesk'] }) {
  const { t } = useTranslation();
  if (!data) return <div className="passport-tab-empty">{t('passport.zendesk.noData')}</div>;
  if (data.tickets.length === 0) return <div className="passport-tab-empty">{t('passport.zendesk.noTickets')}</div>;

  return (
    <div className="passport-proservice-list">
      {data.tickets.map((t: ZendeskTicket) => (
        <ZendeskTicketCard key={t.id} ticket={t} />
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

const PassportPanel: React.FC = () => {
  const { t } = useTranslation();
  const [serialNumber, setSerialNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PassportResult | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('proservice');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const sn = serialNumber.trim();
    if (!sn || loading) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setActiveTab('proservice');
    try {
      const data = await fetchPassport(sn);
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('passport.loadError'));
    } finally {
      setLoading(false);
    }
  }

  const proserviceCount = result?.proservice?.reports.length ?? null;
  const zendeskCount = result?.zendesk?.tickets.length ?? null;

  const tabs: { id: TabId; label: string; count?: number | null }[] = [
    { id: 'proservice', label: t('passport.tabs.proservice'), count: proserviceCount },
    { id: 'fabricode',  label: t('passport.tabs.fabricode') },
    { id: 'zendesk',    label: t('passport.tabs.zendesk'),   count: zendeskCount },
  ];

  return (
    <div className="passport-panel">
      <div className="passport-search-card">
        <div className="passport-card-header">
          <div className="passport-card-title">{t('passport.title')}</div>
          <div className="passport-card-desc">{t('passport.description')}</div>
        </div>
        <form onSubmit={handleSubmit} className="passport-form">
          <input
            type="text"
            className="passport-input"
            placeholder={t('passport.serialPlaceholder')}
            value={serialNumber}
            onChange={(e) => { setSerialNumber(e.target.value); setError(null); }}
            disabled={loading}
            autoFocus
            autoComplete="off"
          />
          <button
            type="submit"
            className="passport-btn"
            disabled={loading || !serialNumber.trim()}
          >
            {loading ? (
              <span className="passport-btn-inner">
                <span className="passport-spinner" />
                {t('passport.loading')}
              </span>
            ) : (
              t('passport.search')
            )}
          </button>
        </form>
        {error && <div className="passport-error">{error}</div>}
      </div>

      {result && (
        <div className="passport-result-card">
          <div className="passport-result-header">
            <span className="passport-result-sn">{t('passport.serialNumberPrefix')} {result.serialNumber}</span>
          </div>

          <div className="passport-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`passport-tab${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
                {tab.count != null && (
                  <span className="passport-tab-badge">{tab.count}</span>
                )}
              </button>
            ))}
          </div>

          <div className="passport-tab-content">
            {activeTab === 'proservice' && <ProServiceTab data={result.proservice} />}
            {activeTab === 'fabricode'  && <FabricodeTab  data={result.fabricode} />}
            {activeTab === 'zendesk'    && <ZendeskTab    data={result.zendesk} />}
          </div>
        </div>
      )}
    </div>
  );
};

export default PassportPanel;
